import { RuleOutcome, RiskVerdict } from './types.js';
/**
 * The deterministic risk engine.
 *
 * Rules decide; AI explains (ADR-005). Nothing in this file talks to a network,
 * a clock, a database, or a random number generator, so a score is a pure
 * function of its snapshot and can be recomputed from what was stored with it.
 *
 * Three properties are enforced by construction and pinned by tests:
 *
 *  1. **Reproducible.** Same snapshot in, same score and same rule list out,
 *     in the same order.
 *
 *  2. **Missing data never helps.** An `unknown` outcome always costs at least
 *     what the corresponding `fail` costs. Otherwise a token engineered to be
 *     unreadable would score better than one that is merely bad, and the
 *     scanner meets tokens engineered exactly that way.
 *
 *  3. **A veto cannot be outvoted.** Facts that make a pair untradeable force
 *     score 0 and verdict `avoid`, however good everything else looks.
 */
/**
 * Ruleset version.
 *
 * Bump on any change to a rule, a penalty, or a verdict band. Stored with every
 * score, because two scores from different rulesets are not comparable and an
 * old score cannot be interpreted without knowing which rules produced it.
 */
export const RULESET_VERSION = '2026-08-22.1';
const MAX_SCORE = 100;
/** Verdict boundaries. Part of the versioned ruleset, not of configuration. */
export const VERDICT_BANDS = {
    /** Below this, `avoid`. */
    avoidBelow: 40,
    /** Below this (and at or above `avoidBelow`), `risky`. */
    riskyBelow: 60,
    /** At or above this, `qualified`. Between the two, `watch`. */
    qualifiedAtOrAbove: 80,
};
/**
 * Penalties.
 *
 * Collected in one table so the weighting is reviewable as a whole rather than
 * scattered through the rules. Each entry gives the cost of a `fail` and of an
 * `unknown`; `unknown` is never cheaper.
 */
const PENALTY = {
    quoteAssetRecognised: { fail: 35, unknown: 35 },
    liquidityAboveMinimum: { fail: 30, unknown: 30 },
    liquidityNonZero: { fail: 40, unknown: 40 },
    pairAgeAboveMinimum: { fail: 20, unknown: 20 },
    tokenMetadataReadable: { fail: 25, unknown: 25 },
    tokenDecimalsSane: { fail: 15, unknown: 15 },
    symbolNotImpersonating: { fail: 45, unknown: 0 },
    feeTierStandard: { fail: 10, unknown: 5 },
};
/** Uniswap V3's standard fee tiers, in hundredths of a bip. */
const STANDARD_FEE_TIERS = [100, 500, 3000, 10000];
/** Plausible ERC-20 decimals. Outside this is not necessarily fatal, only odd. */
const MIN_SANE_DECIMALS = 2;
const MAX_SANE_DECIMALS = 24;
export function assessPair(snapshot, thresholds) {
    const rules = [
        ruleFactoryAllowlisted(snapshot),
        ruleTokensNotBlocklisted(snapshot),
        ruleSymbolNotImpersonating(snapshot),
        ruleQuoteAssetRecognised(snapshot),
        ruleLiquidityNonZero(snapshot),
        ruleLiquidityAboveMinimum(snapshot, thresholds),
        rulePairAgeAboveMinimum(snapshot, thresholds),
        ruleTokenMetadataReadable(snapshot),
        ruleTokenDecimalsSane(snapshot),
        ruleFeeTierStandard(snapshot),
    ];
    const hasVeto = rules.some((rule) => rule.veto);
    const totalPenalty = rules.reduce((sum, rule) => sum + rule.penalty, 0);
    // Integer arithmetic throughout: a score assembled from floating-point
    // subtractions could differ in its last bit between runs, which would break
    // reproducibility for no benefit.
    const score = hasVeto ? 0 : clamp(MAX_SCORE - totalPenalty, 0, MAX_SCORE);
    // Only a rule blocked by *missing configuration* makes the score incomplete.
    // A rule that does not apply to this pair's nature — a fee-tier check on a V2
    // pair — is complete information and must not raise the flag, or every V2
    // pair would read as partial and the badge would stop meaning anything.
    const unconfigured = rules.filter((rule) => rule.unconfigured === true);
    return {
        score,
        verdict: hasVeto ? RiskVerdict.Avoid : verdictForScore(score),
        rulesetVersion: RULESET_VERSION,
        hasVeto,
        rules,
        complete: unconfigured.length === 0,
        unconfiguredCheckIds: unconfigured.map((rule) => rule.id),
        thresholds,
        snapshot,
        verdictBands: { ...VERDICT_BANDS },
    };
}
export function verdictForScore(score) {
    if (score < VERDICT_BANDS.avoidBelow)
        return RiskVerdict.Avoid;
    if (score < VERDICT_BANDS.riskyBelow)
        return RiskVerdict.Risky;
    if (score < VERDICT_BANDS.qualifiedAtOrAbove)
        return RiskVerdict.Watch;
    return RiskVerdict.Qualified;
}
// --- Rules -----------------------------------------------------------------
//
// Each rule is small, independent, and returns a full RuleResult so the stored
// row explains itself without needing this file to interpret it.
/**
 * The pair must come from a factory the operator allowlisted.
 *
 * Veto. Anyone can deploy a contract that emits a well-formed `PairCreated`, so
 * an unrecognised factory is not a low-quality pair — it is not a pair at all
 * as far as this system is concerned.
 */
function ruleFactoryAllowlisted(snapshot) {
    const base = {
        id: 'factory_allowlisted',
        label: 'Factory is allowlisted',
        observed: snapshot.factoryAddress,
    };
    // An empty allowlist means nothing is trusted, never that everything is.
    if (snapshot.allowlistedFactories.length === 0) {
        return {
            ...base,
            outcome: RuleOutcome.Unknown,
            penalty: 0,
            veto: true,
            detail: 'No factory allowlist is configured, so no factory can be trusted. Set ' +
                'ALLOWLISTED_FACTORY_ADDRESSES or SCANNER_FACTORY_ADDRESSES.',
            threshold: null,
        };
    }
    const allowed = snapshot.allowlistedFactories.includes(snapshot.factoryAddress.toLowerCase());
    return {
        ...base,
        outcome: allowed ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: 0,
        veto: !allowed,
        detail: allowed
            ? 'The pair was created by an allowlisted factory.'
            : 'The pair was created by a factory that is not allowlisted. Anyone can emit a pair-creation event, so this pair is not trusted.',
        threshold: null,
    };
}
/**
 * Neither side may be blocklisted.
 *
 * Veto. The operator marked this token as one to never touch, and no amount of
 * liquidity changes that.
 */
function ruleTokensNotBlocklisted(snapshot) {
    const blocked = snapshot.tokens.filter((token) => token.isBlocklisted);
    return {
        id: 'tokens_not_blocklisted',
        label: 'Neither token is blocklisted',
        outcome: blocked.length === 0 ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: 0,
        veto: blocked.length > 0,
        detail: blocked.length === 0
            ? 'Neither token is on the blocklist.'
            : `Blocklisted token in this pair: ${blocked.map((t) => t.address).join(', ')}.`,
        observed: blocked.length,
        threshold: 0,
    };
}
/**
 * A token must not wear a recognised quote asset's symbol without being it.
 *
 * The direct counter to the scam this scanner meets most: deploy a token called
 * `WETH` or `USDC` so the operator's eye reads a familiar name in a Telegram
 * approval card. Metadata sanitisation already strips invisible characters that
 * make two symbols render identically; this catches the plain-text version.
 *
 * A heavy penalty rather than a veto, because a legitimate bridged asset can
 * genuinely share a symbol, and an operator who recognises one should be able to
 * override with their own eyes.
 */
function ruleSymbolNotImpersonating(snapshot) {
    // Checked across every token when the offered side is undetermined: a token
    // wearing a quote asset's symbol is a red flag whichever side it sits on.
    const candidates = snapshot.baseToken !== null ? [snapshot.baseToken] : snapshot.tokens;
    const token = candidates.find((candidate) => candidate.symbol !== null &&
        snapshot.knownQuoteSymbols.includes(candidate.symbol.trim().toUpperCase()) &&
        !snapshot.knownQuoteAddresses.includes(candidate.address.toLowerCase())) ??
        candidates.find((candidate) => candidate.symbol !== null) ??
        null;
    if (token === null || token.symbol === null) {
        return {
            id: 'symbol_not_impersonating',
            label: 'Symbol does not impersonate a quote asset',
            outcome: RuleOutcome.Unknown,
            // No penalty: an absent symbol is already charged by the metadata rule,
            // and charging it twice would double-count one fact.
            penalty: PENALTY.symbolNotImpersonating.unknown,
            veto: false,
            detail: 'No readable symbol to compare, so impersonation could not be assessed.',
            observed: null,
            threshold: null,
        };
    }
    const symbol = token.symbol.trim().toUpperCase();
    const impersonates = snapshot.knownQuoteSymbols.includes(symbol) &&
        !snapshot.knownQuoteAddresses.includes(token.address.toLowerCase());
    return {
        id: 'symbol_not_impersonating',
        label: 'Symbol does not impersonate a quote asset',
        outcome: impersonates ? RuleOutcome.Fail : RuleOutcome.Pass,
        penalty: impersonates ? PENALTY.symbolNotImpersonating.fail : 0,
        veto: false,
        detail: impersonates
            ? `This token calls itself "${symbol}", which is a recognised quote asset's symbol, but it is a different contract. Treat the symbol as hostile and check the address.`
            : 'The symbol does not claim to be a recognised quote asset.',
        observed: symbol,
        threshold: null,
    };
}
/**
 * One side should be a recognised quote asset.
 *
 * Without one the pair cannot be priced in ETH, so no spend limit expressed in
 * ETH can be enforced against it. That is a large penalty rather than a veto so
 * the pair remains visible and the operator can decide.
 */
function ruleQuoteAssetRecognised(snapshot) {
    const recognised = snapshot.quoteTokenAddress !== null;
    return {
        id: 'quote_asset_recognised',
        label: 'Priced in a recognised quote asset',
        outcome: recognised ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: recognised ? 0 : PENALTY.quoteAssetRecognised.fail,
        veto: false,
        detail: recognised
            ? 'One side is a recognised quote asset, so the pair can be priced in ETH.'
            : 'Neither side is a recognised quote asset, so this pair cannot be priced in ETH and ETH-denominated spend limits cannot be applied to it.',
        observed: snapshot.quoteTokenAddress,
        threshold: null,
    };
}
/** There must be some measured liquidity at all. */
function ruleLiquidityNonZero(snapshot) {
    const parsed = parseDecimal(snapshot.liquidityEth);
    if (parsed === null) {
        return {
            id: 'liquidity_non_zero',
            label: 'Liquidity has been measured',
            outcome: RuleOutcome.Unknown,
            penalty: PENALTY.liquidityNonZero.unknown,
            veto: false,
            detail: 'Liquidity could not be measured. An unmeasured pool is treated as an empty one: ' +
                'a pool that cannot be read cannot be traded against safely.',
            observed: null,
            threshold: 0,
        };
    }
    const positive = parsed > 0;
    return {
        id: 'liquidity_non_zero',
        label: 'Liquidity has been measured',
        outcome: positive ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: positive ? 0 : PENALTY.liquidityNonZero.fail,
        veto: false,
        detail: positive
            ? `Measured quote-side liquidity is ${snapshot.liquidityEth} ETH.`
            : 'The pool holds no measurable quote-side liquidity.',
        observed: snapshot.liquidityEth,
        threshold: 0,
    };
}
/** Liquidity must clear the operator's configured floor. */
function ruleLiquidityAboveMinimum(snapshot, thresholds) {
    const id = 'liquidity_above_minimum';
    const label = 'Liquidity clears the configured minimum';
    const minimum = parseDecimal(thresholds.minLiquidityEth);
    if (minimum === null) {
        return {
            id,
            label,
            outcome: RuleOutcome.NotApplicable,
            penalty: 0,
            veto: false,
            detail: 'No MIN_LIQUIDITY_ETH is configured, so this check did not run. Trading cannot be ' +
                'enabled until it is set.',
            observed: snapshot.liquidityEth,
            threshold: null,
            unconfigured: true,
        };
    }
    const measured = parseDecimal(snapshot.liquidityEth);
    if (measured === null) {
        return {
            id,
            label,
            outcome: RuleOutcome.Unknown,
            penalty: PENALTY.liquidityAboveMinimum.unknown,
            veto: false,
            detail: `Liquidity is unmeasured, so it cannot be shown to clear the ${thresholds.minLiquidityEth} ETH minimum.`,
            observed: null,
            threshold: thresholds.minLiquidityEth,
        };
    }
    const clears = measured >= minimum;
    return {
        id,
        label,
        outcome: clears ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: clears ? 0 : PENALTY.liquidityAboveMinimum.fail,
        veto: false,
        detail: clears
            ? `Liquidity of ${snapshot.liquidityEth} ETH clears the ${thresholds.minLiquidityEth} ETH minimum.`
            : `Liquidity of ${snapshot.liquidityEth} ETH is below the ${thresholds.minLiquidityEth} ETH minimum.`,
        observed: snapshot.liquidityEth,
        threshold: thresholds.minLiquidityEth,
    };
}
/**
 * The pair should be old enough to have shown its behaviour.
 *
 * The most dangerous moment to buy is the first seconds of a pool's life, before
 * liquidity removal or a sell-tax flip can be observed.
 */
function rulePairAgeAboveMinimum(snapshot, thresholds) {
    const id = 'pair_age_above_minimum';
    const label = 'Pair is old enough to assess';
    if (thresholds.minPairAgeSeconds === null) {
        return {
            id,
            label,
            outcome: RuleOutcome.NotApplicable,
            penalty: 0,
            veto: false,
            detail: 'No MIN_PAIR_AGE_SECONDS is configured, so this check did not run.',
            observed: snapshot.ageSeconds,
            threshold: null,
            unconfigured: true,
        };
    }
    if (snapshot.ageSeconds === null) {
        return {
            id,
            label,
            outcome: RuleOutcome.Unknown,
            penalty: PENALTY.pairAgeAboveMinimum.unknown,
            veto: false,
            detail: 'The pair creation time is unknown, so its age cannot be established. An unknown age ' +
                'is treated as brand new.',
            observed: null,
            threshold: thresholds.minPairAgeSeconds,
        };
    }
    const oldEnough = snapshot.ageSeconds >= thresholds.minPairAgeSeconds;
    return {
        id,
        label,
        outcome: oldEnough ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: oldEnough ? 0 : PENALTY.pairAgeAboveMinimum.fail,
        veto: false,
        detail: oldEnough
            ? `The pair is ${snapshot.ageSeconds}s old, past the ${thresholds.minPairAgeSeconds}s minimum.`
            : `The pair is only ${snapshot.ageSeconds}s old, under the ${thresholds.minPairAgeSeconds}s minimum. The riskiest moment to buy is the first seconds of a pool's life.`,
        observed: snapshot.ageSeconds,
        threshold: thresholds.minPairAgeSeconds,
    };
}
/** Both tokens should have readable on-chain metadata. */
function ruleTokenMetadataReadable(snapshot) {
    const tokens = snapshot.tokens;
    if (tokens.length === 0) {
        return {
            id: 'token_metadata_readable',
            label: 'Token metadata is readable',
            outcome: RuleOutcome.Unknown,
            penalty: PENALTY.tokenMetadataReadable.unknown,
            veto: false,
            detail: 'No token records were available for this pair.',
            observed: null,
            threshold: null,
        };
    }
    const unreadable = tokens.filter((token) => token.metadataStatus !== 'ok');
    return {
        id: 'token_metadata_readable',
        label: 'Token metadata is readable',
        outcome: unreadable.length === 0 ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: unreadable.length === 0 ? 0 : PENALTY.tokenMetadataReadable.fail,
        veto: false,
        detail: unreadable.length === 0
            ? 'Both tokens returned readable metadata.'
            : `Metadata could not be read for ${unreadable.length} of ${tokens.length} tokens. A contract that will not identify itself is a poor sign.`,
        observed: unreadable.length,
        threshold: 0,
    };
}
/** Decimals should be present and plausible. */
function ruleTokenDecimalsSane(snapshot) {
    const id = 'token_decimals_sane';
    const label = 'Token decimals are plausible';
    // The offered token is what a trade size is computed against, so it is checked
    // when known. With no recognised quote asset the roles are undetermined, but
    // the decimals are still knowable — so every token is checked instead of the
    // rule reporting missing data it does not actually have.
    const candidates = snapshot.baseToken !== null ? [snapshot.baseToken] : [...snapshot.tokens];
    const token = candidates.find((candidate) => candidate.decimals !== null) ?? null;
    if (token === null || token.decimals === null) {
        return {
            id,
            label,
            outcome: RuleOutcome.Unknown,
            penalty: PENALTY.tokenDecimalsSane.unknown,
            veto: false,
            detail: 'The token did not report decimals. Without them an amount cannot be converted ' +
                'correctly, so any trade size would be a guess.',
            observed: null,
            threshold: `${MIN_SANE_DECIMALS}-${MAX_SANE_DECIMALS}`,
        };
    }
    const sane = token.decimals >= MIN_SANE_DECIMALS && token.decimals <= MAX_SANE_DECIMALS;
    return {
        id,
        label,
        outcome: sane ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: sane ? 0 : PENALTY.tokenDecimalsSane.fail,
        veto: false,
        detail: sane
            ? `The token reports ${token.decimals} decimals.`
            : `The token reports ${token.decimals} decimals, outside the plausible ${MIN_SANE_DECIMALS}-${MAX_SANE_DECIMALS} range.`,
        observed: token.decimals,
        threshold: `${MIN_SANE_DECIMALS}-${MAX_SANE_DECIMALS}`,
    };
}
/** A V3 pool's fee tier should be one of the standard ones. */
function ruleFeeTierStandard(snapshot) {
    const id = 'fee_tier_standard';
    const label = 'Fee tier is standard';
    if (snapshot.feeTier === null) {
        return {
            id,
            label,
            outcome: RuleOutcome.NotApplicable,
            penalty: 0,
            veto: false,
            detail: 'This is a V2-style pair, which has no fee tier.',
            observed: null,
            threshold: null,
        };
    }
    const standard = STANDARD_FEE_TIERS.includes(snapshot.feeTier);
    return {
        id,
        label,
        outcome: standard ? RuleOutcome.Pass : RuleOutcome.Fail,
        penalty: standard ? 0 : PENALTY.feeTierStandard.fail,
        veto: false,
        detail: standard
            ? `The pool uses the standard ${snapshot.feeTier / 10_000}% fee tier.`
            : `The pool uses a non-standard fee tier (${snapshot.feeTier}). Unusual, and worth understanding before trading.`,
        observed: snapshot.feeTier,
        threshold: STANDARD_FEE_TIERS.join(', '),
    };
}
// --- Helpers ---------------------------------------------------------------
/**
 * Parse a non-negative decimal string.
 *
 * Returns null for anything unparseable, so a malformed value is treated as
 * missing rather than as zero — zero would be a claim the data does not
 * support.
 *
 * `Number` is acceptable here because the result is only ever compared against
 * a threshold, never stored or displayed. Every value that reaches a database
 * or a screen stays a string.
 */
function parseDecimal(value) {
    if (value === null || value === undefined)
        return null;
    const trimmed = value.trim();
    if (trimmed === '' || !/^\d+(\.\d+)?$/.test(trimmed))
        return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
//# sourceMappingURL=engine.js.map